import {
  type ColumnDef,
  type ColumnFiltersState,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowData,
  type SortingFn,
  type SortingState,
  type Updater,
  useReactTable,
  type VisibilityState
} from "@tanstack/react-table"
import {
  CircleMinus,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  Download,
  FileStack,
  FolderInput,
  MessageCircle,
  SlidersHorizontal,
  Upload,
  Waypoints,
  XIcon,
} from 'lucide-react';
import { Link, useParams } from '@tanstack/react-router';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { toast } from "react-hot-toast"
import { useOpenInCasePartner } from '@/components/case-partner/hooks/useOpenInCasePartner';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { useDeepAnalysisCostConfirmation } from '@/components/deep-analysis/DeepAnalysisCostConfirmation';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"

import { BulkEditFilesDialog, type BulkEditChanges, type BulkEditableFile } from "@/components/ui/bulk-edit-files-dialog"
import { BulkActionBar } from "@/components/ui/bulk-action-bar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DataTableSearchInput,
  type FilenameSuggestion,
  type FilenameSuggestionPage,
} from '@/components/ui/data-table-search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { useTags } from '@/hooks/useTags';
import {
  canToggleDeepAnalysis,
  DEEP_ANALYSIS_LABEL,
  setFileAnalysisSetState,
} from '@/hooks/useAnalysisSet';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { documentNewTabUrl } from '@/lib/documentRoutes';
import { processDroppedDataTransfer } from '@/lib/dragDropUtils';
import { isExternalSyncRestrictedSource } from '@/lib/sourceUtils';
import { cn, downloadFile } from '@/lib/utils';
import { toolbarActionLabelClass, toolbarContainerClass } from '@/lib/toolbar-compact';
import type { DocumentDeleteOptions } from '@/types/document';
import { AddToBundleDialog } from '../bundle/AddToBundleDialog';
import type { AffectedBundle } from "../bundle/bundleTypes";
import { MoveDocumentsDialog } from "../document-types"
import { FOLDER_FIRST_SORT_COLUMN_ID } from '@/components/folders/folder-table-utils';
import { Loading } from '../shared';
import { FileUpload, SupportedFileTypesText } from "../shared/FileUpload"
import { getSearchHighlightTerms, highlightText } from './column-utilities';
import { DeleteButton } from "./delete-button"
import ExportButton from "./export-button"
import {
  FilterClearAllButton,
  FilterMenuActiveBar,
  FilterMenuButton,
  QuickFilterTags,
  ToolbarCompactProvider,
  filterActiveBarHasContent,
  type ColumnFilterMeta,
  type FilterField,
  type QuickFilterPreset,
} from './data-table-filter-toolbar'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  hasPopupMenu?: boolean;
  uploadButton?: boolean;
  exportButton?: boolean;
  documentType?: string;
  documentTypeSlug?: string;
  folderGuid?: string;  // Optional folder GUID for folder-based filtering
  /** Matter guid when the table is used off `/matter/$slug` (e.g. home document picker). */
  matterGuid?: string;
  searchPlaceholder?: string;
  /** Show server-backed filename matches beneath the search input. */
  enableFilenameSuggestions?: boolean;
  hideSearch?: boolean;
  onUploadComplete?: () => void;
  onRowsSelected?: (rows: string[]) => void;
  onDelete?: (selectedIds: string[], options?: DocumentDeleteOptions) => void | Promise<void>;
  getDeleteAttachmentCount?: (rows: TData[]) => number;
  isDeleting?: boolean;
  syncButton?: React.ReactNode;
  isLoading?: boolean;
  onDropFiles?: (files: File[]) => Promise<void>;
  /** Called when files are dropped directly onto an inline folder row, routing the upload to that folder */
  onFilesDroppedOnFolder?: (files: File[], folderId: number) => void;
  isProcessingDrop?: boolean;
  onEmptyStateClick?: () => void;
  renderSubComponent?: (row: Row<TData>) => React.ReactElement;
  /** Renders sub-rows as actual <tr> elements for column alignment with parent rows */
  renderSubRows?: (row: Row<TData>) => React.ReactNode;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getSubRows?: (row: TData) => TData[] | undefined;
  activeFiltersRef?: React.MutableRefObject<any>;
  showQuickFilters?: boolean;
  /** Field config for the Filters menu (Status/Assignee/Party/Date, etc.) */
  filterFields?: FilterField[];
  /** Extra controls rendered next to the Filters button. */
  toolbarExtra?: React.ReactNode;
  /** Extra classes on the filter toolbar, for example more space above the table. */
  toolbarClassName?: string;
  /** Extra active-filter count from toolbarExtra (e.g. default source folders). */
  toolbarExtraActiveCount?: number;
  /** Called when the toolbar "Clear all" resets filters. */
  onClearAllFilters?: () => void;
  /** One-tap quick-filter presets shown at the top of the Filters menu */
  quickFilterPresets?: QuickFilterPreset[];
  /** Column filters applied when the table first mounts (e.g. a default date window). */
  initialColumnFilters?: ColumnFiltersState;
  /** Column visibility applied when the table first mounts (e.g. hide a filter-only column). */
  initialColumnVisibility?: VisibilityState;
  onExpandedRowsChange?: (expandedRowIds: string[]) => void;
  expandedRowsRef?: React.MutableRefObject<{ expandRows: (rowIds: string[]) => void; collapseRows?: (rowIds: string[]) => void }>;
  onFiltersChange?: () => void;
  useFixedLayout?: boolean;
  initialPageSize?: number;
  /** Selection mode: 'internal' uses table state, 'external' uses onRowClick callback */
  selectionMode?: 'internal' | 'external';
  /** Called when a row is clicked in external selection mode */
  onRowClick?: (row: TData) => void;
  /** Optional row click handler invoked for all selection modes (receives the click event) */
  onAnyRowClick?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /**
   * Called when a row body is clicked, regardless of selection mode. Unlike
   * `onRowClick` (external mode only), this coexists with internal checkbox
   * selection: interactive cell controls stop propagation, so it only fires on
   * clicks in the row's non-interactive areas. Used to open a detail side panel.
   */
  onRowActivate?: (row: TData) => void;
  /** Called when visible rows change (pagination, filtering). Returns array of row data for current page */
  onVisibleRowsChange?: (rows: TData[]) => void;
  /** When this key changes, all column filters will be reset */
  resetFiltersKey?: string | number;
  /** Render prop for custom popup menu content. When provided, replaces default correspondence-specific buttons */
  popupMenuContent?: (props: {
    selectedCount: number;
    selectedRows: TData[];
    onDelete: () => void;
    isDeleting: boolean;
    clearSelection: () => void;
  }) => React.ReactNode;
  /** Enables bulk tag edits where single-file tag editing is supported */
  enableBulkTags?: boolean;
  /** Label used in the delete confirmation dialog (default: "file") */
  deleteConfirmationLabel?: string;
  /** Called when a row is double-clicked. If not provided, double-click does nothing. */
  onRowDoubleClick?: (row: TData) => void;
  /** Optional action element rendered right-aligned in the quick filters row */
  headerAction?: React.ReactNode;
  /** Optional callback to add CSS classes to individual rows */
  getRowClassName?: (row: TData) => string | undefined;
  /** Whether Deep Analysis actions should be shown. Default: false */
  deepAnalysisEnabled?: boolean;
}

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500] as const;

type SearchResult = {
  id: string;
  fileName: string;
  content: string;
  rank: number;
  exactMatchScore: number,
};

type SearchResultState = Pick<
  SearchResult,
  'content' | 'rank' | 'exactMatchScore'
>;

type FilenameSuggestionResponse = {
  document_guid: string;
  file_reference_guid: string;
  filename: string;
};

type FilenameSuggestionPageResponse = {
  results: FilenameSuggestionResponse[];
  next_offset: number | null;
};

const FILENAME_SUGGESTIONS_PAGE_SIZE = 20;

const mapToSearchResult = (data: any, rank: number): SearchResult => {
  try {
    return {
      id: data.file_reference_guid || data.document_guid || '',
      fileName: data.filename || '',
      content: data.content || '',
      rank,
      exactMatchScore: data.scores.exact_text_match || 0.0,
    };
  } catch (err) {
    console.error('Error in mapToSearchResult:', {
      metadata: data.metadata,
      error: (err as Error).message,
    });
    throw err;
  }
};

declare module '@tanstack/table-core' {
  interface SortingFns {
    myCustomSorting: SortingFn<unknown>
  }

  interface ColumnMeta<TData extends RowData, TValue> {
    /** Enable row double-click behavior for this column */
    enableRowDoubleClick?: boolean;
    /** Enable click-to-copy behavior for this column */
    enableClickToCopy?: boolean;
    /** Custom CSS class for header cells */
    headerClassName?: string;
    /** Custom CSS class for body cells */
    cellClassName?: string;
    /** Declarative filter descriptor consumed by `deriveFilterFields` for the toolbar. */
    filter?: ColumnFilterMeta;
  }

  interface TableMeta<TData extends RowData> {
    /** True when filtering is handled by the toolbar Filters menu, so columns hide their inline header filters. */
    filtersInToolbar?: boolean;
    /** Shift-click range selection handler for checkbox columns. */
    handleCheckboxClick?: (rowId: string, event: React.MouseEvent) => void;
  }
}

function DataTableComponent<TData extends { id?: string, guid?: string, is_draft?: boolean, is_drafting_managed?: boolean, draft_document_guid?: string | null, document_type?: any, token_count?: number, tags?: unknown[], displayName?: string, fileName?: string, analysisSetEffective?: boolean, analysisSetStatus?: string | null }, TValue>({
  columns,
  data,
  hasPopupMenu,
  uploadButton,
  exportButton,
  documentType,
  documentTypeSlug,
  folderGuid,
  matterGuid,
  searchPlaceholder,
  enableFilenameSuggestions = false,
  hideSearch = false,
  onUploadComplete,
  onRowsSelected,
  onDelete,
  getDeleteAttachmentCount,
  isDeleting: isParentDeleting,
  syncButton,
  isLoading = false,
  onDropFiles,
  onFilesDroppedOnFolder,
  isProcessingDrop = false,
  onEmptyStateClick,
  renderSubComponent,
  renderSubRows,
  getRowCanExpand: customGetRowCanExpand,
  getSubRows,
  activeFiltersRef,
  showQuickFilters = false,
  filterFields,
  toolbarExtra,
  toolbarClassName,
  toolbarExtraActiveCount = 0,
  onClearAllFilters,
  quickFilterPresets,
  initialColumnFilters,
  initialColumnVisibility,
  onExpandedRowsChange,
  expandedRowsRef,
  onFiltersChange,
  initialPageSize = DEFAULT_PAGE_SIZE,
  enableBulkTags = false,
  useFixedLayout = false,
  selectionMode = 'internal',
  onRowClick,
  onAnyRowClick,
  onRowActivate,
  onVisibleRowsChange,
  resetFiltersKey,
  popupMenuContent,
  deleteConfirmationLabel = 'file',
  onRowDoubleClick,
  headerAction,
  getRowClassName,
  deepAnalysisEnabled = false,
}: DataTableProps<TData, TValue>) {
  const { activeDocumentGuid, openDocumentPanel } = useSidePanel();
  const {
    confirmDeepAnalysisCost,
    deepAnalysisCostConfirmationDialog,
  } = useDeepAnalysisCostConfirmation();

  // When the table contains inline folder rows, keep them pinned above document rows
  // regardless of the active sort by prepending a hidden folder-first sort key.
  const hasFolderRows = useMemo(
    () => data.some((row) => Boolean((row as { isFolderRow?: boolean }).isFolderRow)),
    [data]
  );
  const ensureFolderFirstSorting = useCallback((nextSorting: SortingState): SortingState => {
    if (!hasFolderRows) return nextSorting;
    return [
      { id: FOLDER_FIRST_SORT_COLUMN_ID, desc: true },
      ...nextSorting.filter((sort) => sort.id !== FOLDER_FIRST_SORT_COLUMN_ID),
    ];
  }, [hasFolderRows]);

  const [sorting, setSorting] = useState<SortingState>([])
  // Folder rows are pinned via the hidden `__folderFirst` key. Deriving the effective sorting here
  // (rather than relying on `onSortingChange`) guarantees folders stay on top on initial load and
  // whenever folder rows appear — otherwise documents with a null date sort above the folder
  // sentinel date under the default `date desc` sort.
  const sortingWithFolderFirst = useMemo(
    () => ensureFolderFirstSorting(sorting),
    [ensureFolderFirstSorting, sorting],
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    () => initialColumnFilters ?? []
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  })
  // Keep the user on page 1 of the *filtered* set. `autoResetPageIndex` is off so
  // selection-driven data/column refreshes don't kick them back to page 1, but that
  // also leaves pageIndex stale when a filter shrinks the result (e.g. Assignee on
  // page 2 of Deadlines → empty table even though matches exist on page 1).
  const handleColumnFiltersChange = useCallback((updater: Updater<ColumnFiltersState>) => {
    setColumnFilters(updater);
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [])
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(() => ({
      ...(initialColumnVisibility ?? {}),
      [FOLDER_FIRST_SORT_COLUMN_ID]: false,
    }))
  const [rowSelection, setRowSelection] = useState({})
  const [activeQuickFilter, setActiveQuickFilter] = useState<'overdue' | 'finished' | 'open' | null>(null);

  // Update activeFiltersRef when column filters change
  useEffect(() => {
    if (activeFiltersRef) {
      const filters: any = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      activeFiltersRef.current = filters;

      // Notify parent component that filters changed
      if (onFiltersChange) {
        onFiltersChange();
      }
    }
  }, [columnFilters, activeFiltersRef, onFiltersChange]);

  // Detect manual filter changes and deactivate quick filter
  useEffect(() => {
    if (activeQuickFilter && columnFilters.length > 0) {
      // Check if the current state filter matches the quick filter
      const stateFilter = columnFilters.find(f => f.id === 'state');
      if (stateFilter) {
        const stateValue = stateFilter.value as string[];
        let matchesQuickFilter = false;

        switch (activeQuickFilter) {
          case 'overdue':
            matchesQuickFilter = stateValue.length === 1 && stateValue[0] === 'Overdue';
            break;
          case 'finished':
            matchesQuickFilter = stateValue.length === 1 && stateValue[0] === 'Completed';
            break;
          case 'open':
            matchesQuickFilter = stateValue.length === 2 &&
              stateValue.includes('Confirmed') &&
              stateValue.includes('Completed');
            break;
        }

        if (!matchesQuickFilter) {
          setActiveQuickFilter(null);
        }
      } else {
        // State filter was removed
        setActiveQuickFilter(null);
      }
    }
  }, [columnFilters, activeQuickFilter]);

  // Drag and drop state
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Folder id of the inline folder row currently being dragged over (for per-folder drop highlight).
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const dragCounterRef = useRef(0);

  // Token limit check
  const { tokenLimitInfo } = useTokenLimit();

  const [activeSearchQuery, setActiveSearchQuery] = useState<string | null>(null);
  const activeSearchHighlightTerms = React.useMemo(
    () => getSearchHighlightTerms(activeSearchQuery),
    [activeSearchQuery],
  );
  const [searchInputResetKey, setSearchInputResetKey] = useState(0);
  const [searchResults, setSearchResults] = useState<Record<string, SearchResultState>>({});
  const [isSearching, setIsSearching] = useState(false);

  const [expanded, setExpanded] = useState<ExpandedState>({})

  // Notify parent component when expanded rows change
  useEffect(() => {
    if (onExpandedRowsChange) {
      const expandedIds = Object.keys(expanded).filter(
        key => (expanded as Record<string, boolean>)[key]
      );
      onExpandedRowsChange(expandedIds);
    }
  }, [expanded, onExpandedRowsChange]);

  // Expose expand function through ref
  useEffect(() => {
    if (expandedRowsRef) {
      expandedRowsRef.current = {
        expandRows: (rowIds: string[]) => {
          setExpanded(prev => {
            const newExpanded: ExpandedState = typeof prev === 'object' ? { ...prev } : {};
            rowIds.forEach(id => {
              newExpanded[id] = true;
            });
            return newExpanded;
          });
        },
        collapseRows: (rowIds: string[]) => {
          setExpanded(prev => {
            if (typeof prev !== 'object') return prev;
            const newExpanded: ExpandedState = { ...prev };
            rowIds.forEach(id => {
              delete newExpanded[id];
            });
            return newExpanded;
          });
        }
      };
    }
  }, [expandedRowsRef]);

  const { slug: routeSlug } = useParams({ strict: false });
  const slug =
    matterGuid
    ?? (typeof routeSlug === 'string' && routeSlug && routeSlug !== 'undefined' ? routeSlug : undefined);
  const openInCasePartner = useOpenInCasePartner();
  const shouldEnableBulkFileActions = Boolean(hasPopupMenu && !popupMenuContent);
  const shouldEnableBulkTags = shouldEnableBulkFileActions && enableBulkTags;
  const {
    tags: bulkTagOptions,
    isLoading: isLoadingBulkTags,
  } = useTags(shouldEnableBulkTags ? slug : null);

  const lastSelectedRowId = useRef<string | null>(null);

  const table = useReactTable({
    data,
    columns,
    enableColumnFilters: true,
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const nextSorting = typeof updater === 'function' ? updater(prev) : updater;
        return ensureFolderFirstSorting(nextSorting);
      });
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows,
    // Inline folder rows are not selectable — clicking them navigates into the folder.
    enableRowSelection: (row) => !(row.original as { isFolderRow?: boolean }).isFolderRow,
    enableSubRowSelection: true,
    getRowCanExpand: customGetRowCanExpand || ((row) => row.id in searchResults),
    getRowId: (row, index) => String(row.id ?? row.guid ?? `row-${index}`),
    autoResetPageIndex: false, // Prevent page reset when data/columns change (e.g., during selection)
    state: {
      sorting: sortingWithFolderFirst,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded: expanded,
      pagination,
    },
    onExpandedChange: setExpanded,
    paginateExpandedRows: false,
    meta: {
      filtersInToolbar: !!(filterFields && filterFields.length > 0),
      handleCheckboxClick: (rowId: string, event: React.MouseEvent) => {
        const rows = table.getRowModel().rows;
        const clickedVisualIndex = rows.findIndex(r => r.id === rowId);
        if (clickedVisualIndex < 0) return;
        if (event.shiftKey && lastSelectedRowId.current) {
          const anchorIndex = rows.findIndex(r => r.id === lastSelectedRowId.current);
          if (anchorIndex >= 0) {
            const start = Math.min(anchorIndex, clickedVisualIndex);
            const end = Math.max(anchorIndex, clickedVisualIndex);
            for (let i = start; i <= end; i++) {
              const row = rows[i];
              if (!row?.getCanSelect()) continue;
              row.toggleSelected(true);
            }
          }
        }
        lastSelectedRowId.current = rowId;
      },
    },
    sortingFns: {
      myCustomSorting: (rowA, rowB, columnId) => {
        const valueA = rowA.getValue(columnId) as number;
        const valueB = rowB.getValue(columnId) as number;

        const rankA = searchResults[valueA]?.rank ?? Number.MAX_SAFE_INTEGER;
        const rankB = searchResults[valueB]?.rank ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      },
    }
  })

  useEffect(() => {
    if (!table.getColumn("date") || sorting.some((sort) => sort.id === "date")) {
      return;
    }

    setSorting([
      ...sorting,
      {
        id: "date",
        desc: true,
      },
    ]);
  }, [table, sorting]);

  // Notify parent of visible rows when they change (for external selection mode)
  const visibleRows = table.getRowModel().rows;
  useEffect(() => {
    if (onVisibleRowsChange) {
      onVisibleRowsChange(visibleRows.map(row => row.original));
    }
  }, [visibleRows, onVisibleRowsChange]);

  // Reset table state when resetFiltersKey changes (used when navigating folders)
  const prevResetFiltersKeyRef = useRef(resetFiltersKey);
  useEffect(() => {
    if (resetFiltersKey !== undefined && resetFiltersKey !== prevResetFiltersKeyRef.current) {
      table.resetColumnFilters();
      table.setPageIndex(0);
      setSearchResults({});
      setActiveSearchQuery(null);
      setSearchInputResetKey((key) => key + 1);
      setActiveQuickFilter(null);
      prevResetFiltersKeyRef.current = resetFiltersKey;
    }
  }, [resetFiltersKey, table]);

  function handleReset() {
    table.resetColumnFilters();
    setSearchResults({})
    setActiveSearchQuery(null);
    setSearchInputResetKey((key) => key + 1);
    setActiveQuickFilter(null);
    onClearAllFilters?.();
  }

  // Quick filter functions for tasks
  const applyQuickFilter = (filterType: 'overdue' | 'finished' | 'open') => {
    const stateColumn = table.getColumn('state');
    if (!stateColumn) return;

    // If clicking the same filter, deactivate it
    if (activeQuickFilter === filterType) {
      table.resetColumnFilters();
      setActiveQuickFilter(null);
      return;
    }

    // Reset all filters first, then apply the quick filter
    table.resetColumnFilters();
    setActiveQuickFilter(filterType);

    switch (filterType) {
      case 'overdue':
        stateColumn.setFilterValue(['Overdue']);
        break;
      case 'finished':
        stateColumn.setFilterValue(['Completed']);
        break;
      case 'open':
        stateColumn.setFilterValue(['Confirmed', 'Completed']);
        break;
    }
  };

  const clearSearchQuery = () => {
    setSearchInputResetKey((key) => key + 1);
    handleColumnFiltersChange((filters) => filters.filter((filter) => filter.id !== 'id'));
    setExpanded({});
    setSearchResults({});
    setActiveSearchQuery(null);
  };

  const handleSearchSubmit = React.useCallback((query: string) => {
    if (query === activeSearchQuery) return;

    // A new search replaces the previous semantic search. Remove its ID filter
    // before collecting the rows constrained by the remaining column filters.
    setIsSearching(true);
    handleColumnFiltersChange((filters) => filters.filter((filter) => filter.id !== 'id'));
    setExpanded({});
    setSearchResults({});
    setActiveSearchQuery(query);
  }, [activeSearchQuery, handleColumnFiltersChange]);

  const loadFilenameSuggestions = React.useCallback(async (
    query: string,
    offset: number,
  ): Promise<FilenameSuggestionPage> => {
    if (!slug) {
      return { suggestions: [], nextOffset: null };
    }

    const response = await backendClient.get(
      `/correspondence/${slug}/filename-suggestions`,
      {
        params: {
          q: query,
          limit: FILENAME_SUGGESTIONS_PAGE_SIZE,
          offset,
          ...(folderGuid ? { folder_guid: folderGuid } : {}),
        },
      },
    );

    const page = response.data as FilenameSuggestionPageResponse;
    return {
      suggestions: (page.results || []).map((result) => ({
        documentGuid: result.document_guid,
        fileReferenceGuid: result.file_reference_guid,
        filename: result.filename,
      })),
      nextOffset: page.next_offset,
    };
  }, [folderGuid, slug]);

  const handleFilenameSuggestionSelect = React.useCallback((suggestion: FilenameSuggestion) => {
    openDocumentPanel({ documentGuid: suggestion.fileReferenceGuid, activeTab: 'document' });
  }, [openDocumentPanel]);

  useEffect(() => {
    let isCurrentSearch = true;

    if (activeSearchQuery && slug) {
      const fetchData = async () => {
        setIsSearching(true);

        try {
          // Get the current filtered document GUIDs. Exclude synthetic inline folder rows: their
          // `guid` is a folder guid, not a document guid, and posting it would skew search results.
          const filteredDocumentGuids = table.getFilteredRowModel().flatRows
            .filter((row) => !(row.original as { isFolderRow?: boolean }).isFolderRow)
            .map((row) => row.original.guid)
            .filter(Boolean);

          const response = await backendClient.post(
            `/correspondence/${slug}/semantic-search`,
            {
              query: activeSearchQuery,
              document_guids: filteredDocumentGuids.length > 0 ? filteredDocumentGuids : undefined,
            },
            { params: folderGuid ? { folder_guid: folderGuid } : undefined }
          );

          if (!isCurrentSearch) return;

          // Preserve the backend's canonical ordering, including exact-match
          // priority and its lexical/vector tie-breakers.
          const searchResultData = response.data.results.map(
            (result: any, index: number) => mapToSearchResult(result, index + 1),
          );

          const resDict: Record<string, SearchResultState> = {};
          const selectionDict: { [key: string]: boolean } = {};

          if (searchResultData) {
            searchResultData.forEach((result: SearchResult) => {
                resDict[result.id] = {
                  content: result.content,
                  rank: result.rank,
                  exactMatchScore: result.exactMatchScore,
                };
                selectionDict[result.id] = true;
            });

            setSearchResults(resDict);

            const uniqueIds = Object.keys(resDict);

            // Expand each matched row. For documents nested under a parent row, also expand
            // every ancestor so the match stays visible.
            const expandedState: Record<string, boolean> = {};
            table.getCoreRowModel().flatRows.forEach((tableRow) => {
              if (!selectionDict[tableRow.id]) return;
              expandedState[tableRow.id] = true;
              let parentRow = tableRow.getParentRow?.();
              while (parentRow) {
                expandedState[parentRow.id] = true;
                parentRow = parentRow.getParentRow?.();
              }
            });

            // table.setRowSelection(selectionDict);
            table.setExpanded(expandedState);
            table.getColumn("id")?.setFilterValue(uniqueIds);

            table.setSorting([
              {
                id: "id", // Ensure this column ID matches your column definition in 'columns'.
                desc: false, // Lower backend ranks are more relevant.
              },
            ]);

            setIsSearching(false);
          }

        } catch (error: any) {
          if (!isCurrentSearch) return;

          console.error('Failed to fetch semantic search data:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Status code:', error.response.status);

            // Show appropriate error message based on status code
            if (error.response.status === 503) {
              toast.error('Search service is initializing. Please try again in a moment.');
            } else if (error.response.status === 500) {
              toast.error('Internal server error. The search index may be building. Please try again in a few minutes.');
            } else {
              toast.error(`Search failed: ${error.response.data?.detail || 'Unknown error'}`);
            }
          } else {
            toast.error('Failed to connect to search service. Please try again later.');
          }
          setIsSearching(false);
          return [];
        }
      };

      fetchData();
    } else {
      setSearchResults({})
      setIsSearching(false);
    }

    return () => {
      isCurrentSearch = false;
    };
  }, [activeSearchQuery, slug, folderGuid]);

  const isFiltered = table.getState().columnFilters.length > 0
  const filterExtraActiveCount = (activeSearchQuery ? 1 : 0) + toolbarExtraActiveCount;
  const showFilterActiveBar = Boolean(
    filterFields &&
      filterFields.length > 0 &&
      filterActiveBarHasContent(table, filterFields, quickFilterPresets, filterExtraActiveCount),
  );
  const showLegacyResetFilters = !showQuickFilters &&
    (isFiltered || activeSearchQuery) &&
    !(filterFields && filterFields.length > 0);
  const showSecondaryToolbarRow = Boolean(
    activeSearchQuery || showFilterActiveBar || showLegacyResetFilters,
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAttachments, setDeleteAttachments] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [isDeletingLocal, setIsDeletingLocal] = useState(false);
  const deleteInFlightRef = useRef(false);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [isApplyingBulkEdit, setIsApplyingBulkEdit] = useState(false);
  const [isMarkingDeepAnalysis, setIsMarkingDeepAnalysis] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const selectedDocumentRows = table.getSelectedRowModel().flatRows.filter(
    (row) => !(row.original as { isFolderRow?: boolean }).isFolderRow,
  );
  const selectedFilesForBulkEdit: BulkEditableFile[] = selectedDocumentRows.map((row) => ({
    id: row.original.id || row.original.guid || row.id,
    guid: row.original.guid,
    is_draft: row.original.is_draft,
    is_drafting_managed: row.original.is_drafting_managed,
    // The bulk-edit path is files-only, where `tags` are string labels; the
    // loosened generic constraint widens this to unknown[] for reuse by other
    // row types (e.g. deadline Tasks), so narrow it back here.
    tags: row.original.tags as string[] | undefined,
    displayName: row.original.displayName,
    fileName: row.original.fileName,
  }));
  const selectedRowCount = selectedDocumentRows.length;
  const selectedRowsForDelete = selectedDocumentRows.map((row) => row.original);
  const selectedDeleteAttachmentCount = getDeleteAttachmentCount?.(selectedRowsForDelete) ?? 0;

  const openDeleteDialog = () => {
    const selectedRows = selectedDocumentRows.map((row) => row.original);
    const attachmentCount = getDeleteAttachmentCount?.(selectedRows) ?? 0;
    setDeleteAttachments(attachmentCount > 0);
    setDeleteDialogOpen(true);
  };
  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const hasSelectedRows = selectedRowCount > 0;
  const [affectedBundles, setAffectedBundles] = useState<AffectedBundle[]>([]);
  const { ui_labels } = useMatterJurisdiction().jurisdiction;

  // Drag and drop event handlers
  useEffect(() => {
    if ((!onDropFiles && !onFilesDroppedOnFolder) || isProcessingDrop || !tokenLimitInfo.isWithinLimit) return;

    // Resolve the inline folder row (if any) under the drag/drop target. Folder rows tag
    // themselves with `data-folder-drop-id` so files can be routed to that folder specifically.
    const resolveFolderDropId = (target: EventTarget | null): number | null => {
      const el = (target as HTMLElement | null)?.closest?.('[data-folder-drop-id]');
      if (!el) return null;
      const parsed = Number(el.getAttribute('data-folder-drop-id'));
      return Number.isFinite(parsed) ? parsed : null;
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current++;

      // Only highlight if actually dragging files
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDraggingOver(true);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }

      // Track which folder row (if any) is being hovered so it can be highlighted.
      setDragOverFolderId(onFilesDroppedOnFolder ? resolveFolderDropId(e.target) : null);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounterRef.current--;

      // Only remove highlight when all drag events are complete
      if (dragCounterRef.current === 0) {
        setIsDraggingOver(false);
        setDragOverFolderId(null);
      }
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset drag counter and visual state
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
      const folderDropId = onFilesDroppedOnFolder ? resolveFolderDropId(e.target) : null;
      setDragOverFolderId(null);

      try {
        if (!e.dataTransfer) return;
        const files = await processDroppedDataTransfer(e.dataTransfer);
        if (files.length === 0) {
          toast.error('No files could be read from the drop. Try selecting them using the upload button.');
          return;
        }
        // Route to the specific folder row when one was targeted; otherwise fall back to the
        // table-level drop (current folder).
        if (folderDropId != null && onFilesDroppedOnFolder) {
          onFilesDroppedOnFolder(files, folderDropId);
        } else if (onDropFiles) {
          await onDropFiles(files);
        }
      } catch (error) {
        console.error('Error processing dropped items:', error);
        toast.error('Failed to read the dropped files. Please try again.');
      }
    };

    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener('dragenter', handleDragEnter);
      tableElement.addEventListener('dragover', handleDragOver);
      tableElement.addEventListener('dragleave', handleDragLeave);
      tableElement.addEventListener('drop', handleDrop);
    }

    return () => {
      if (tableElement) {
        tableElement.removeEventListener('dragenter', handleDragEnter);
        tableElement.removeEventListener('dragover', handleDragOver);
        tableElement.removeEventListener('dragleave', handleDragLeave);
        tableElement.removeEventListener('drop', handleDrop);
      }
    };
  }, [onDropFiles, onFilesDroppedOnFolder, isProcessingDrop, tokenLimitInfo.isWithinLimit]);

  const handleDelete = async (force = false) => {
    if (deleteInFlightRef.current || isParentDeleting) return;

    deleteInFlightRef.current = true;
    setIsDeletingLocal(true);

    try {
      const selectedIds = selectedDocumentRows.map(row => row.getValue("id") as string);
      const selectedRows = selectedDocumentRows.map((row) => row.original);
      const attachmentCount = getDeleteAttachmentCount?.(selectedRows) ?? 0;

      await onDelete?.(selectedIds, {
        force,
        deleteAttachments: attachmentCount > 0 && deleteAttachments,
      });
      table.toggleAllRowsSelected(false);
      setAffectedBundles([]);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      const bundleWarning = error?.response?.status === 409 &&
        error?.response?.data?.detail?.warnings?.find((w: any) => w.type === 'bundle_references');
      if (bundleWarning) {
        setAffectedBundles(bundleWarning.affected_bundles);
        return;
      }
      // Other errors: surface to user (callers re-throw without toasting)
      toast.error(apiErrorMessage(error) || 'Failed to delete files');
      setDeleteDialogOpen(false);
    } finally {
      deleteInFlightRef.current = false;
      setIsDeletingLocal(false);
    }
  };

  const clearSelectedRows = () => {
    table.toggleAllRowsSelected(false);
  };

  const handleApplyBulkEdit = async ({
    draftTarget,
    draftDocumentGuids,
    tagDocumentGuids,
    tagGuids,
  }: BulkEditChanges) => {
    if (!slug || (draftDocumentGuids.length === 0 && tagDocumentGuids.length === 0)) return;

    setIsApplyingBulkEdit(true);
    try {
      if (draftTarget !== undefined && draftDocumentGuids.length > 0) {
        await backendClient.post(`/correspondence/${slug}/toggle-draft-status`, {
          document_guids: draftDocumentGuids,
          is_draft: draftTarget,
        });
      }

      if (shouldEnableBulkTags && tagGuids.length > 0 && tagDocumentGuids.length > 0) {
        await backendClient.post(`/tags/${slug}/bulk-apply`, {
          document_guids: tagDocumentGuids,
          tag_guids: tagGuids,
        });
      }

      const statusMessage = draftDocumentGuids.length > 0
        ? `Updated status on ${draftDocumentGuids.length} file${draftDocumentGuids.length !== 1 ? 's' : ''}`
        : null;
      const tagMessage = tagDocumentGuids.length > 0
        ? `added tags to ${tagDocumentGuids.length} file${tagDocumentGuids.length !== 1 ? 's' : ''}`
        : null;

      toast.success(
        statusMessage && tagMessage
          ? `${statusMessage} and ${tagMessage}`
          : statusMessage || `Added tags to ${tagDocumentGuids.length} file${tagDocumentGuids.length !== 1 ? 's' : ''}`
      );
      onUploadComplete?.();
      setBulkEditDialogOpen(false);
      clearSelectedRows();
    } catch (error) {
      console.error('Failed to apply bulk edit:', error);
      toast.error(apiErrorMessage(error) || 'Failed to apply bulk edit');
    } finally {
      setIsApplyingBulkEdit(false);
    }
  };

  const handleUpdateSelectedDeepAnalysis = async () => {
    if (!slug || !deepAnalysisEnabled) return;

    const selectedRows = selectedDocumentRows;
    const toggleableRows = selectedRows.filter((row) =>
      canToggleDeepAnalysis(row.original.analysisSetStatus),
    );
    const skippedCount = selectedRows.length - toggleableRows.length;

    if (toggleableRows.length === 0) {
      toast.error(`Selected files are still being updated in ${DEEP_ANALYSIS_LABEL}`);
      return;
    }

    const shouldUnmark = toggleableRows.every((row) => row.original.analysisSetEffective === true);
    const nextState = shouldUnmark ? 'excluded' : 'included';
    const targetRows = toggleableRows
      .filter((row) => shouldUnmark || row.original.analysisSetEffective !== true);
    const selectedFileReferenceIds = targetRows
      .map((row) => row.original.id || (row.getValue("id") as string | undefined))
      .filter((id): id is string => Boolean(id));

    if (selectedFileReferenceIds.length === 0) {
      toast.success(`Selected files are already marked for ${DEEP_ANALYSIS_LABEL}`);
      clearSelectedRows();
      return;
    }

    if (nextState === 'included') {
      const tokenCount = targetRows.reduce((sum, row) => (
        sum + (row.original.token_count || 0)
      ), 0);
      const confirmed = await confirmDeepAnalysisCost({
        documentCount: selectedFileReferenceIds.length,
        tokenCount,
      });
      if (!confirmed) return;
    }

    setIsMarkingDeepAnalysis(true);
    try {
      await Promise.all(
        selectedFileReferenceIds.map((fileReferenceId) =>
          setFileAnalysisSetState(slug, fileReferenceId, nextState)
        )
      );
      const skippedSuffix = skippedCount > 0
        ? `; skipped ${skippedCount} pending file${skippedCount !== 1 ? 's' : ''}`
        : '';
      toast.success(
        shouldUnmark
          ? `Removed ${selectedFileReferenceIds.length} file${selectedFileReferenceIds.length !== 1 ? 's' : ''} from ${DEEP_ANALYSIS_LABEL}${skippedSuffix}`
          : `Marked ${selectedFileReferenceIds.length} file${selectedFileReferenceIds.length !== 1 ? 's' : ''} for ${DEEP_ANALYSIS_LABEL}${skippedSuffix}`
      );
      onUploadComplete?.();
      clearSelectedRows();
    } catch (error) {
      console.error('Failed to update selected files for deep analysis:', error);
      toast.error(apiErrorMessage(error) || `Failed to update ${DEEP_ANALYSIS_LABEL}`);
    } finally {
      setIsMarkingDeepAnalysis(false);
    }
  };

  const handleBatchDownload = async () => {
    const selectedRows = selectedDocumentRows;
    if (selectedRows.length === 0 || !slug) return;

    setIsDownloading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const row of selectedRows) {
        const documentId = row.getValue("id") as string;

        try {
          const response = await backendClient.get(
            `/correspondence/${slug}/get-file?file_reference_guid=${documentId}&download=true`
          );
          const downloadInfo = response.data;

          const downloadFilename = downloadInfo.fileName || downloadInfo.displayName || 'download';
          await downloadFile(downloadInfo.signedUrl, downloadFilename);
          successCount++;

          if (selectedRows.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (error) {
          console.error(`Failed to download document ${documentId}:`, error);
          failCount++;
        }
      }

      if (failCount === 0) {
        toast.success(`Started ${successCount} download${successCount !== 1 ? 's' : ''}`);
      } else if (successCount > 0) {
        toast.success(`Started ${successCount} download${successCount !== 1 ? 's' : ''}, ${failCount} failed`);
      } else {
        toast.error('Failed to download files');
      }
    } catch (error) {
      console.error('Batch download error:', error);
      toast.error('Failed to download files');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full">
      <style>{`
        .hide-column-filters button[aria-haspopup="dialog"] {
          display: none !important;
        }
      `}</style>
      <div className={cn('flex flex-col gap-1.5 w-full pb-2', toolbarClassName)}>
        {/* Primary row: search + filters button, actions on the right. The search
            group deliberately has no `min-w-0` — its min-content width is what
            makes the actions wrap to their own row rather than overflow the
            (usually `overflow-x-hidden`) page container. */}
        <ToolbarCompactProvider className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 w-full min-w-0', toolbarContainerClass)}>
          {(!hideSearch || (filterFields && filterFields.length > 0) || toolbarExtra) && (
            <div className="flex items-center gap-2 flex-1">
              {!hideSearch && (
                <div className="flex-1 min-w-44 sm:max-w-xs">
                  <DataTableSearchInput
                    key={searchInputResetKey}
                    placeholder={searchPlaceholder}
                    isSearching={isSearching}
                    onSubmit={handleSearchSubmit}
                    loadSuggestions={enableFilenameSuggestions ? loadFilenameSuggestions : undefined}
                    onSuggestionSelect={enableFilenameSuggestions ? handleFilenameSuggestionSelect : undefined}
                  />
                </div>
              )}
              {filterFields && filterFields.length > 0 && (
                <FilterMenuButton table={table} fields={filterFields} />
              )}
              {toolbarExtra}
              {quickFilterPresets && quickFilterPresets.length > 0 && (
                <>
                  <div className="mx-0.5 hidden h-4 w-px bg-border sm:block" aria-hidden />
                  <div className="flex items-center gap-0.5">
                    <QuickFilterTags table={table} presets={quickFilterPresets} />
                  </div>
                </>
              )}
              {filterFields && filterFields.length > 0 && (
                <FilterClearAllButton
                  table={table}
                  fields={filterFields}
                  onClearAll={handleReset}
                  extraActiveCount={filterExtraActiveCount}
                />
              )}
            </div>
          )}

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {syncButton}
            {exportButton &&
              <ExportButton />
            }
            {uploadButton && slug && (
              <FileUpload
                matterGuid={slug}
                onUploadComplete={onUploadComplete}
                className="ml-2"
                showAsDialog
              />
            )}
          </div>
        </ToolbarCompactProvider>

        {/* Secondary row: active search chip + filter chips wrap independently */}
        {showSecondaryToolbarRow && (
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {activeSearchQuery && (
              <div className="rounded-xl border-2 border-dashed w-full max-w-xs xl:w-auto xl:max-w-md h-auto max-h-[52px] flex flex-wrap items-center p-1 custom-scrollbar gap-1 overflow-y-auto">
                <div className="inline-flex items-center bg-gray-100 text-black font-medium text-sm rounded-xl px-3">
                  {activeSearchQuery}
                  <button
                    type="button"
                    className="ml-2 text-black hover:text-gray-700 font-medium focus:outline-hidden"
                    aria-label="Remove"
                    onClick={clearSearchQuery}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {showFilterActiveBar && filterFields && (
              <FilterMenuActiveBar
                table={table}
                fields={filterFields}
                quickFilters={quickFilterPresets}
              />
            )}
            {showLegacyResetFilters && (
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isSearching}
                className="h-8 px-2 lg:px-3 border-dashed"
                size="sm"
              >
                Reset filters
                <XIcon className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>


      {((showQuickFilters && !filterFields) || headerAction) && (
      <div className="flex flex-row w-full items-center justify-between pt-2 sm:pt-4 lg:pt-6">
        <div className="flex items-center">
          {showQuickFilters && !filterFields && (
            <div className="pb-2 pt-2">
              <div className="flex justify-center items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600 mr-2">Quick filters:</span>
                <Button
                  variant={activeQuickFilter === 'overdue' ? 'default' : 'outline'}
                  onClick={() => applyQuickFilter('overdue')}
                  className="h-8 px-3"
                  size="sm"
                >
                  Overdue
                </Button>
                <Button
                  variant={activeQuickFilter === 'open' ? 'default' : 'outline'}
                  onClick={() => applyQuickFilter('open')}
                  className="h-8 px-3"
                  size="sm"
                >
                  Confirmed
                </Button>
                <Button
                  variant={activeQuickFilter === 'finished' ? 'default' : 'outline'}
                  onClick={() => applyQuickFilter('finished')}
                  className="h-8 px-3"
                  size="sm"
                >
                  Completed
                </Button>
                {isFiltered && !activeQuickFilter && (
                  <Button
                    variant="outline"
                    onClick={() => handleReset()}
                    className="h-8 px-2 lg:px-3 border-dashed ml-2"
                    size="sm"
                  >
                    Reset
                    <XIcon className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {headerAction && <div>{headerAction}</div>}
      </div>
      )}
      <div
        ref={tableRef}
        className={`rounded-md border transition-all duration-150 ${
          isDraggingOver
            ? 'border-crimson-400 border-2 bg-crimson-400/5 shadow-lg'
            : ''
        } ${activeQuickFilter ? 'hide-column-filters' : ''}`}
      >
        <Table style={useFixedLayout ? { tableLayout: 'fixed', width: '100%' } : undefined}>
          <TableHeader className={hasSelectedRows ? "[&_tr]:bg-slate-200!" : undefined}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={header.column.columnDef.meta?.headerClassName}
                      style={useFixedLayout ? {
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        maxWidth: header.column.columnDef.maxSize,
                      } : undefined}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    key={row.id}
                    data-folder-drop-id={
                      onFilesDroppedOnFolder &&
                      (row.original as { isFolderRow?: boolean; folderId?: number }).isFolderRow &&
                      (row.original as { folderId?: number }).folderId != null
                        ? String((row.original as { folderId?: number }).folderId)
                        : undefined
                    }
                    data-state={
                      activeDocumentGuid && row.getValue("id") === activeDocumentGuid
                        ? "highlighted"
                        : row.getIsSelected()
                          ? "selected"
                          : undefined
                    }
                    className={`group bg-white ${(selectionMode === 'external' || onRowActivate) ? 'cursor-pointer hover:bg-gray-50' : ''} ${getRowClassName?.(row.original) ?? ''} ${
                      dragOverFolderId != null &&
                      (row.original as { folderId?: number }).folderId === dragOverFolderId
                        ? 'ring-2 ring-inset ring-blue-500 bg-blue-50!'
                        : ''
                    }`}
                    onClick={(event) => {
                      if (selectionMode === 'external' && onRowClick) {
                        onRowClick(row.original);
                        return;
                      }
                      if (onAnyRowClick) {
                        onAnyRowClick(row.original, event);
                      }
                      if (!onRowActivate) return;
                      // Ignore clicks on inline controls so in-place edits don't open the panel.
                      // Include dialog/listbox options: edit dialogs are mounted inside rows, and
                      // React portals still bubble clicks through the React tree.
                      if (
                        (event.target as Element | null)?.closest?.(
                          'button, a, input, textarea, select, label, [role="menuitem"], [role="menuitemcheckbox"], [role="checkbox"], [role="option"], [role="listbox"], [role="dialog"], [role="alertdialog"], [contenteditable="true"]',
                        )
                      ) {
                        return;
                      }
                      if ((row.original as { isFolderRow?: boolean }).isFolderRow) return;
                      onRowActivate(row.original);
                    }}
                    onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell, visibleIndex) => (
                      <TableCell
                        key={cell.id}
                        className={cell.column.columnDef.meta?.cellClassName}
                        style={useFixedLayout ? {
                          width: cell.column.getSize(),
                          minWidth: cell.column.columnDef.minSize,
                          maxWidth: cell.column.columnDef.maxSize,
                        } : undefined}
                      >
                        {row.depth > 0 && visibleIndex === 1 ? (
                          <div style={{ transform: `translateX(${row.depth * 16}px)` }}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Render sub-rows as actual <tr> elements for column alignment */}
                  {row.getIsExpanded() && renderSubRows && renderSubRows(row)}
                  {/* Render sub-component in a colspan cell (legacy behavior) */}
                  {row.getIsExpanded() && renderSubComponent && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={row.getAllCells().length}>
                        {renderSubComponent(row)}
                      </TableCell>
                    </TableRow>
                  )}
                  {row.getIsExpanded() && searchResults[row.getValue("id") as string] && (
                    <tr>
                      <td colSpan={row.getAllCells().length}>
                        <div className="relative p-4 border rounded-lg bg-gray-50">
                          {searchResults[row.getValue("id") as string].exactMatchScore === 1 && (
                            <div className="absolute top-4 right-4 flex items-center justify-center">
                              <Badge variant="default">Exact Match</Badge>
                            </div>
                          )}
                          <Link
                            to="/matter/$slug/f/$fileId"
                            params={{ slug: slug ?? '', fileId: row.getValue("id") as string }}
                            search={{ from: `case-files/${folderGuid ? `folder/${folderGuid}` : documentTypeSlug}` }}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {/* <p className="text-sm text-gray-700 font-semibold">{row.getValue("id")}</p> */}
                          </Link>
                          <div className="mt-4">
                            <p className="text-sm text-gray-700 mb-2">Most relevant passage:</p>
                            <div className="ml-2 p-3 rounded-sm bg-muted">
                              <div className="text-sm text-gray-700 wrap-break-word">
                                {highlightText(
                                  searchResults[row.getValue("id") as string].content,
                                  activeSearchHighlightTerms,
                                  { matchMode: "word-prefix" },
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end mt-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                const from = `case-files/${folderGuid ? `folder/${folderGuid}` : documentTypeSlug}`;
                                const fromQuery = `from=${encodeURIComponent(from)}`;
                                const fallbackUrl = `/matter/${slug}/f/${row.getValue("id")}`;
                                const baseUrl = slug && row.original.id
                                  ? documentNewTabUrl(slug, {
                                    id: row.original.id,
                                    is_drafting_managed: row.original.is_drafting_managed,
                                    draft_document_guid: row.original.draft_document_guid,
                                  })
                                  : fallbackUrl;
                                const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${fromQuery}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                              }}
                            >
                              Open Document
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Loading text={`Loading ${documentType?.toLowerCase() || 'documents'}`}/>
                    </div>
                  ) : (
                    onDropFiles && !isProcessingDrop ? (
                      <div
                        className="flex flex-col items-center justify-center gap-4 cursor-pointer"
                        onClick={tokenLimitInfo.isWithinLimit ? onEmptyStateClick : undefined}
                      >
                        <div className="p-8">
                          {tokenLimitInfo.isWithinLimit && (
                            <Upload className={`h-8 w-8 mx-auto mb-2 ${isDraggingOver ? 'text-crimson' : 'text-slate-400'}`} />
                          )}
                          <p className={`text-sm mb-1 ${!tokenLimitInfo.isWithinLimit ? 'text-slate-400' : isDraggingOver ? 'text-crimson font-medium' : 'text-slate-600'}`}>
                            {!tokenLimitInfo.isWithinLimit ? 'Upload disabled - Token limit exceeded' : 'Drag files here or click to upload'}
                          </p>
                          {!tokenLimitInfo.isWithinLimit ? (
                            <p className="text-xs text-slate-500">Contact support@crimson.law to increase your limit</p>
                          ) : (
                            <SupportedFileTypesText className="text-xs text-slate-500" />
                          )}
                        </div>
                      </div>
                    ) : (
                      "No results."
                    )
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className={`flex items-center justify-between px-2 mt-2 pb-2 ${hasSelectedRows && "mb-16"}`}>
        <div className="flex-1 text-sm text-muted-foreground">
          {hasSelectedRows ? (
            <>
              {selectedRowCount} of{" "}
              {filteredRowCount} {filteredRowCount === 1 ? "row" : "rows"} selected
            </>
          ) : (
            <>
              {filteredRowCount} {filteredRowCount === 1 ? "row" : "rows"} total
            </>
          )}
        </div>
        {table.getPageCount() > 1 && (
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={(value) => {
                  table.setPageSize(Number(value))
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={table.getState().pagination.pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {PAGE_SIZE_OPTIONS.map((pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRightIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {hasPopupMenu && (
        <BulkActionBar open={hasSelectedRows}>
            {popupMenuContent ? (
              popupMenuContent({
                selectedCount: selectedRowCount,
                selectedRows: selectedDocumentRows.map((row) => row.original),
                onDelete: openDeleteDialog,
                isDeleting: isParentDeleting || isDeletingLocal,
                clearSelection: clearSelectedRows,
              })
            ) : (
            <div className="flex flex-row gap-2 justify-between items-center w-full min-w-0">
              <div className='flex gap-2 items-center'>
                {(() => {
                  const selectedRows = selectedDocumentRows;
                  const toggleableDeepAnalysisRows = selectedRows.filter((row) =>
                    canToggleDeepAnalysis(row.original.analysisSetStatus),
                  );
                  const shouldUnmarkDeepAnalysis = toggleableDeepAnalysisRows.length > 0
                    && toggleableDeepAnalysisRows.every((row) => row.original.analysisSetEffective === true);
                  const deepAnalysisDisabled = isParentDeleting
                    || isDeletingLocal
                    || isApplyingBulkEdit
                    || isMarkingDeepAnalysis
                    || toggleableDeepAnalysisRows.length === 0;
                  const deepAnalysisActionLabel = shouldUnmarkDeepAnalysis
                    ? 'Remove from Deep Analysis'
                    : 'Mark for Deep Analysis';
                  const deepAnalysisLoadingLabel = shouldUnmarkDeepAnalysis
                    ? 'Removing...'
                    : 'Marking...';

                  return (
                    <>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          openInCasePartner(slug, selectedRows.map(row => row.original));
                        }}
                        icon={MessageCircle}
                        text="Open in Case Partner"
                        aria-label="Open in Case Partner"
                      />
                      {deepAnalysisEnabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleUpdateSelectedDeepAnalysis}
                          disabled={deepAnalysisDisabled}
                          icon={shouldUnmarkDeepAnalysis ? CircleMinus : Waypoints}
                          text={deepAnalysisActionLabel}
                          loading={isMarkingDeepAnalysis}
                          loadingText={deepAnalysisLoadingLabel}
                          aria-label={deepAnalysisActionLabel}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
              <div className='flex gap-2 items-center'>
                {(() => {
                  const selectedRows = selectedDocumentRows;
                  const hasRestrictedSourceFiles = selectedRows.some((row) => {
                    const source = String((row.original as Record<string, unknown>)?.source || '').toLowerCase();
                    return isExternalSyncRestrictedSource(source);
                  });

                  const bundleLabel = ui_labels.bundle_singular;
                  const bundleButton = (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBundleDialogOpen(true)}
                      disabled={isParentDeleting || isDeletingLocal}
                      icon={FileStack}
                      text={bundleLabel}
                      textClassName={toolbarActionLabelClass}
                      tooltip={bundleLabel}
                      aria-label={bundleLabel}
                    />
                  );

                  const moveButton = (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMoveDialogOpen(true)}
                      disabled={isParentDeleting || isDeletingLocal || hasRestrictedSourceFiles}
                      icon={FolderInput}
                      text="Move"
                      textClassName={toolbarActionLabelClass}
                      tooltip={hasRestrictedSourceFiles ? undefined : "Move"}
                      aria-label="Move"
                    />
                  );

                  const deleteButton = (
                    <DeleteButton
                      onClick={openDeleteDialog}
                      isDeleting={isParentDeleting || isDeletingLocal}
                      disabled={hasRestrictedSourceFiles}
                      textClassName={toolbarActionLabelClass}
                      tooltip={hasRestrictedSourceFiles ? false : "Delete"}
                    />
                  );

                  const wrapWithTooltip = (button: React.ReactNode, message: string) => (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex" tabIndex={0}>{button}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[240px] text-xs font-normal">
                          {message}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );

                  const downloadLabel = isDownloading ? 'Downloading...' : 'Download';
                  const downloadDisabled = isParentDeleting || isDeletingLocal || isDownloading;
                  const downloadButton = (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBatchDownload}
                      disabled={downloadDisabled}
                      icon={Download}
                      text="Download"
                      loading={isDownloading}
                      loadingText="Downloading..."
                      textClassName={toolbarActionLabelClass}
                      tooltip={downloadDisabled ? undefined : downloadLabel}
                      aria-label="Download"
                    />
                  );

                  return (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBulkEditDialogOpen(true)}
                        disabled={isParentDeleting || isDeletingLocal || isApplyingBulkEdit || isMarkingDeepAnalysis}
                        icon={SlidersHorizontal}
                        text="Edit"
                        textClassName={toolbarActionLabelClass}
                        tooltip="Edit"
                        aria-label="Edit"
                      />

                      {bundleButton}

                      {downloadDisabled
                        ? wrapWithTooltip(downloadButton, downloadLabel)
                        : downloadButton}

                      {hasRestrictedSourceFiles
                        ? wrapWithTooltip(moveButton, 'One or more selected files are synced from OneDrive/SharePoint or iManage and cannot be moved')
                        : moveButton}

                      {hasRestrictedSourceFiles
                        ? wrapWithTooltip(deleteButton, 'One or more selected files are synced from OneDrive/SharePoint or iManage and cannot be deleted')
                        : deleteButton}
                    </>
                  );
                })()}
              </div>
            </div>
            )}
        </BulkActionBar>
      )}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { if (!open) { setDeleteDialogOpen(false); setAffectedBundles([]); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected {selectedDocumentRows.length} {deleteConfirmationLabel}{selectedDocumentRows.length > 1 ? 's' : ''} from Crimson.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedDeleteAttachmentCount > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-stone-200 bg-stone-50 p-3 text-sm">
              <Checkbox
                id="bulk-delete-attachments"
                checked={deleteAttachments}
                onCheckedChange={(checked) => setDeleteAttachments(checked === true)}
                disabled={isParentDeleting || isDeletingLocal}
              />
              <label htmlFor="bulk-delete-attachments" className="leading-5 cursor-pointer">
                <span className="font-medium text-stone-900">Also delete attachments from the selected emails</span>
                <span className="block text-stone-600">
                  Uncheck to delete only the selected files and keep their attachments.
                </span>
              </label>
            </div>
          )}
          {affectedBundles.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <p className="font-medium mb-1">Some selected files are used in the following {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()}:</p>
              <ul className="list-disc pl-4 space-y-1.5">
                {affectedBundles.map(b => (
                  <li key={b.bundle_id}>{b.bundle_title} ({b.affected_file_count} file{b.affected_file_count > 1 ? 's' : ''})</li>
                ))}
              </ul>
              <p className="mt-1">Deleting will remove these files from {affectedBundles.length === 1 ? `this ${ui_labels.bundle_singular.toLowerCase()}` : `these ${ui_labels.bundle_plural.toLowerCase()}`}. The {affectedBundles.length > 1 ? ui_labels.bundle_plural.toLowerCase() : ui_labels.bundle_singular.toLowerCase()} may need to be regenerated.</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isParentDeleting || isDeletingLocal}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(affectedBundles.length > 0); }}
              disabled={isParentDeleting || isDeletingLocal}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
            >
              {isParentDeleting || isDeletingLocal ? 'Deleting...' : affectedBundles.length > 0 ? 'Delete Anyway' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move documents dialog */}
      <MoveDocumentsDialog
        isOpen={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        documentIds={selectedDocumentRows.map(row => row.getValue("id") as string)}
        onSuccess={() => {
          // Call the parent's onUploadComplete function to refresh the data
          onUploadComplete?.();
        }}
        currentFolderIds={Array.from(new Set(
          selectedDocumentRows
            .map(row => row.original.document_type?.id)
            .filter((id): id is string => id !== undefined)
            .map(id => parseInt(id))
            .filter(id => !isNaN(id))
        ))}
        matterSlug={slug}
      />

      {slug ? (
        <AddToBundleDialog
          open={bundleDialogOpen}
          onOpenChange={setBundleDialogOpen}
          selectedDocuments={selectedDocumentRows.map(row => {
            const original = row.original as { displayName?: string; fileName?: string };
            return {
              id: row.getValue("id") as string,
              name: original.displayName || original.fileName,
            };
          })}
          matterGuid={slug}
        />
      ) : null}

      <BulkEditFilesDialog
        open={bulkEditDialogOpen}
        onOpenChange={setBulkEditDialogOpen}
        selectedFiles={selectedFilesForBulkEdit}
        enableTags={shouldEnableBulkTags}
        tags={bulkTagOptions}
        isLoadingTags={isLoadingBulkTags}
        isApplying={isApplyingBulkEdit}
        onApply={handleApplyBulkEdit}
      />
      {deepAnalysisCostConfirmationDialog}
    </div>
  )
}

// Export memoized DataTable to prevent unnecessary re-renders
export const DataTable = React.memo(DataTableComponent) as typeof DataTableComponent;
